import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import fr from 'facet-rest';
import { buildFacetDesignOverride } from 'Packages/FacetRest/FacetDesignOverride';
import {
  FDBodyText, FDContentContainer, FDStack,
} from '@facet-wealth/design';
import PageHeader from 'Components/ComponentLibrary/Molecules/PageHeader';
import MetaForm from 'Components/ComponentLibrary/Organisms/MetaForm';
import MetaFormLayout
  from 'Views/Page/Content/Templates/FacetPlanClickToSync/AccountMapping/AccountMappingMetaFormLayout';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import StepNavigator from 'Components/ComponentLibrary/Molecules/StepNavigator';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import { useSnackbarContext } from 'Context/SnackbarContext';

const styles = (theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    minHeight: '100dvh',
    padding: theme.spacing(0, 4),
  },
  formContainer: {
    width: '75%',
    maxWidth: '75%',
  },
  navigation: {
    position: 'sticky',
    bottom: 0,
    width: '100%',
    backgroundColor: theme.palette.common.white,
    display: 'flex',
    alignItems: 'center',
    zIndex: 1,
    flexDirection: 'column',
  },
});

const FacetPlanClickToSyncAccountMappingTemplate = ({
  classes,
  resource,
}) => {
  const [loading, setLoading] = useState(false);
  const { loadPageContentLink, isPageContentLoading } = usePageContentContext();
  const { displayErrorMessage } = useSnackbarContext();
  const handleOnNext = async () => {
    setLoading(true);
    try {
      const updatedColl = await resource.submit();
      await updatedColl.resources().head().withRelationship(
        fr.relationships.scenarioChangeReview.name,
        async (rel) => {
          return loadPageContentLink(rel.link('related'));
        },
        () => null,
      );
    } catch (e) {
      displayErrorMessage(e, e.errors);
      setLoading(false);
    }
    setLoading(false);
  };

  const onPrevious = resource.withRelationship(
    fr.relationships.facetPlanView.name,
    (rel) => async () => {
      setLoading(true);
      try {
        await loadPageContentLink(rel.link('related'));
      } catch (e) {
        setLoading(false);
        displayErrorMessage(e, e.errors);
      }
      setLoading(false);
    },
    () => null,
  );

  return (
    <Box className={classes.root}>
      <FDStack alignItems="center" direction="column" flexGrow={1} spacing={4}>
        <PageHeader title={resource.attr('title')} />
        <FDContentContainer maxWidth="lg">
          <FDBodyText value={resource.attr('subtitle')} />
        </FDContentContainer>
        <MetaForm
          className={classNames(classes.formContainer)}
          collection={resource.attr('virtualAccountMappings')}
          metaOverrideSet={buildFacetDesignOverride()}
          layout={MetaFormLayout}
        />
      </FDStack>
      <div className={classes.navigation}>
        <StepNavigator
          onNext={handleOnNext}
          onPrevious={onPrevious}
          isLoading={isPageContentLoading || loading}
        >
          {resource.attr('primaryActionLabel')}
        </StepNavigator>
      </div>
    </Box>
  );
};

FacetPlanClickToSyncAccountMappingTemplate.defaultProps = {};

FacetPlanClickToSyncAccountMappingTemplate.propTypes = {
  classes: PropTypes.object.isRequired,
  resource: PropTypes.instanceOf(fr.Resource).isRequired,
};

export default withStyles(styles)(FacetPlanClickToSyncAccountMappingTemplate);
